import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import 'leaflet/dist/leaflet.css';

import DocumentUpload from './components/DocumentUpload';
import EntityDashboard from './components/EntityDashboard';
import GeographicMap from './components/GeographicMap';
import MitreHeatmap from './components/MitreHeatmap';
import SpartaHeatmap from './components/SpartaHeatmap';
import AgentChat from './components/AgentChat';
import RecentDocuments from './components/RecentDocuments';
import OtelDataDictionary from './components/OtelDataDictionary';
import CywareIntelDashboard from './components/CywareIntelDashboard';
import agentService, { EventType } from './services/AgentService';
import { fetchGeoEntities } from './services/geoEntityService';

import './App.css';

function App() {
    const [documents, setDocuments] = useState([]);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [entities, setEntities] = useState({});
    const [entityCounts, setEntityCounts] = useState({});
    const [searchResults, setSearchResults] = useState([]);
    const [activeTab, setActiveTab] = useState('dashboard');

    useEffect(() => {
        // Load recent documents on startup
        fetchRecentDocuments();
        // Load existing entity data from GraphQL
        loadExistingEntityData();
    }, []);

    // Listen for agent export events and auto-trigger downloads
    useEffect(() => {
        const handleAgentEvent = (event) => {
            // Check if this is a custom export trigger event
            if (event.type === EventType.CUSTOM && event.customType === 'TRIGGER_EXPORT_DOWNLOAD') {
                console.log('Auto-triggering export download:', event.exportType);
                
                // Trigger the appropriate export button by finding and clicking it
                setTimeout(() => {
                    const buttons = document.querySelectorAll('button');
                    buttons.forEach(button => {
                        const buttonText = button.textContent;
                        if (event.exportType === 'pdf' && buttonText.includes('Export PDF Report')) {
                            button.click();
                        } else if (event.exportType === 'stix' && buttonText.includes('Export STIX JSON')) {
                            button.click();
                        } else if (event.exportType === 'otel' && buttonText.includes('Export OTEL JSON')) {
                            button.click();
                        }
                    });
                }, 500); // Small delay to ensure agent message is displayed first
            }
        };

        // Subscribe to agent events
        const unsubscribe = agentService.subscribe({ onEvent: handleAgentEvent });

        // Cleanup subscription on unmount
        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, []);

    const loadExistingEntityData = async () => {
        try {
            // Use backend proxy for GraphQL to work in all contexts (browser + Playwright)
            const response = await fetch('/api/graphql', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    query: `{
                        entityCounts {
                            threatActors
                            countries
                            cves
                            mitreTechniques
                            systems
                        }
                        intelCategories {
                            name
                            intel_count
                        }
                    }`
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.data && data.data.entityCounts) {
                    const counts = data.data.entityCounts;
                    const cywareCategories = data.data.intelCategories || [];
                    
                    // Calculate total Cyware intel
                    const totalCywareIntel = cywareCategories.reduce((sum, cat) => sum + cat.intel_count, 0);

                    // Real country/location names from Neo4j for the Geographic Map
                    const geoEntities = await fetchGeoEntities().catch((geoError) => {
                        console.error('Failed to load geographic entities:', geoError);
                        return { countries: [], locations: [] };
                    });
                    
                    // Create mock document with existing data
                    const mockDocument = {
                        id: 'existing-data',
                        filename: 'Existing Neo4j Data',
                        entities: {
                            threatActors: Array(counts.threatActors).fill(0).map((_, i) => `APT${29 - i}`),
                            countries: geoEntities.countries,
                            locations: geoEntities.locations,
                            cves: Array(counts.cves).fill(0).map((_, i) => `CVE-2023-${1234 + i}`),
                            mitreTechniques: Array(counts.mitreTechniques).fill(0).map((_, i) => `T${1566 + i}.001`),
                            systems: Array(counts.systems).fill(0).map((_, i) => ['Microsoft Exchange', 'Windows Server', 'Active Directory', 'SharePoint', 'SQL Server'][i] || `System${i}`),
                            cywareIntel: Array(totalCywareIntel).fill(0).map((_, i) => `Intel-${i + 1}`)
                        },
                        summary: `Cybersecurity threat intelligence data loaded from Neo4j database. Includes ${totalCywareIntel} Cyware S-ISAC intel reports.`,
                        processedAt: new Date().toISOString()
                    };

                    // Add Cyware count to entity counts
                    const enhancedCounts = {
                        ...counts,
                        cywareIntel: totalCywareIntel
                    };

                    setSelectedDocument(mockDocument);
                    setEntities(mockDocument.entities);
                    setEntityCounts(enhancedCounts);
                    setDocuments(prev => [mockDocument, ...prev]);
                }
            }
        } catch (error) {
            console.error('Failed to load existing entity data:', error);
        }
    };

    const fetchRecentDocuments = async () => {
        try {
            const response = await fetch('/api/documents/recent');
            if (response.ok) {
                const recentDocs = await response.json();
                setDocuments(recentDocs);
            }
        } catch (error) {
            console.error('Failed to fetch recent documents:', error);
        }
    };

    const handleDocumentProcessed = (documentData) => {
        console.log('Document processed:', documentData);
        console.log('Document entities:', documentData.entities);
        
        const newDocument = {
            id: documentData.documentId || documentData.id,
            title: documentData.title || 'Untitled Document',
            filename: documentData.filename,
            entities: documentData.entities || {},
            summary: documentData.summary,
            processedAt: new Date().toISOString()
        };

        setDocuments(prev => [newDocument, ...prev.slice(0, 9)]); // Keep last 10
        setSelectedDocument(newDocument);
        setEntities(documentData.entities || {});
        setEntityCounts(calculateEntityCounts(documentData.entities || {}));
        setActiveTab('sparta'); // Switch to SPARTA tab to see results
    };

    const handleDocumentSelected = (document) => {
        console.log('Document selected:', document);
        console.log('Selected document entities:', document.entities);
        setSelectedDocument(document);
        setEntities(document.entities || {});
        setEntityCounts(calculateEntityCounts(document.entities || {}));
        setActiveTab('sparta'); // Switch to SPARTA tab to see results
    };

    const exportPdfReport = async () => {
        try {
            // Show loading indicator
            const loadingDiv = document.createElement('div');
            loadingDiv.id = 'pdf-loading-indicator';
            loadingDiv.innerHTML = `
                <div style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; 
                            background: rgba(0,0,0,0.7); z-index: 10000; 
                            display: flex; align-items: center; justify-content: center;">
                    <div style="background: white; padding: 30px; border-radius: 10px; text-align: center;">
                        <div class="spinner-border text-primary" role="status" style="width: 3rem; height: 3rem;">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                        <h4 class="mt-3">Generating PDF Report</h4>
                        <p class="text-muted">Capturing all dashboard tabs...<br/>This may take 20-30 seconds</p>
                    </div>
                </div>
            `;
            document.body.appendChild(loadingDiv);

            const reportData = {
                timestamp: new Date().toISOString(),
                entities: entities,
                entityCounts: entityCounts,
                selectedDocument: selectedDocument,
                searchResults: searchResults,
                summary: generateDashboardSummary(),
                hasDocument: !!selectedDocument  // Flag to indicate if document is selected
            };

            const response = await fetch('/api/export-pdf', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(reportData)
            });

            // Remove loading indicator
            const loader = document.getElementById('pdf-loading-indicator');
            if (loader) loader.remove();

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                a.download = `cybersecurity-report-${new Date().toISOString().split('T')[0]}.pdf`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                
                console.log('✅ PDF export completed successfully!');
            } else {
                const errorText = await response.text();
                console.error('Failed to export PDF report:', errorText);
                alert('Failed to generate PDF report. Please check that data is loaded and try again.');
            }
        } catch (error) {
            console.error('Failed to export PDF report:', error);
            
            // Remove loading indicator on error
            const loader = document.getElementById('pdf-loading-indicator');
            if (loader) loader.remove();
            
            alert('Error generating PDF report: ' + error.message);
        }
    };

    const exportOtelJson = async () => {
        try {
            // Use backend endpoint for OTEL JSON export
            const response = await fetch('/api/export-otel-json', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({})
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                a.download = `otel-cybersecurity-data-${new Date().toISOString().split('T')[0]}.json`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                console.log('OTEL JSON export completed successfully!');
            } else {
                const errorData = await response.json();
                console.error('Failed to export OTEL JSON:', errorData.error || 'Unknown error');
            }
        } catch (error) {
            console.error('Failed to export OTEL JSON:', error);
        }
    };

    const exportStixJson = async () => {
        try {
            // Use backend endpoint for STIX 2.1 JSON export
            const response = await fetch('/api/export-stix-json', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ tlp_level: 'GREEN' })
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                a.download = `stix-threat-intelligence-${new Date().toISOString().split('T')[0]}.json`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                console.log('STIX 2.1 JSON export completed successfully!');
            } else {
                const errorData = await response.json();
                console.error('Failed to export STIX JSON:', errorData.error || 'Unknown error');
            }
        } catch (error) {
            console.error('Failed to export STIX JSON:', error);
        }
    };

    const generateOtelSpans = (entities) => {
        const spans = [];
        let spanId = 1;

        // Generate spans for each entity type
        Object.entries(entities).forEach(([entityType, entityList]) => {
            if (Array.isArray(entityList)) {
                entityList.forEach((entity, index) => {
                    spans.push({
                        traceId: `cyber-trace-${entityType}`,
                        spanId: `span-${spanId++}`,
                        name: `${entityType}.${entity.name || entity.cveId || entity.techniqueId || `item-${index}`}`,
                        kind: 1, // SPAN_KIND_INTERNAL
                        startTimeUnixNano: Date.now() * 1000000,
                        endTimeUnixNano: Date.now() * 1000000,
                        attributes: [
                            { key: "entity.type", value: { stringValue: entityType } },
                            { key: "entity.name", value: { stringValue: entity.name || entity.cveId || entity.techniqueId || 'unknown' } },
                            { key: "entity.description", value: { stringValue: entity.description || 'No description available' } },
                            ...(entity.aliases ? [{ key: "entity.aliases", value: { arrayValue: { values: entity.aliases.map(alias => ({ stringValue: alias })) } } }] : []),
                            ...(entity.severity ? [{ key: "cve.severity", value: { stringValue: entity.severity } }] : []),
                            ...(entity.type ? [{ key: "system.type", value: { stringValue: entity.type } }] : []),
                            ...(entity.version ? [{ key: "system.version", value: { stringValue: entity.version } }] : []),
                            ...(entity.isoCode ? [{ key: "country.iso_code", value: { stringValue: entity.isoCode } }] : [])
                        ]
                    });
                });
            }
        });

        return spans;
    };

    const generateDashboardSummary = () => {
        const totalEntities = Object.values(entityCounts).reduce((sum, count) => sum + count, 0);
        return {
            totalEntities,
            documentName: selectedDocument?.filename || 'No document selected',
            analysisDate: new Date().toISOString(),
            entityBreakdown: entityCounts,
            topThreats: entities.threatActors?.slice(0, 5) || [],
            criticalCves: entities.cves?.filter(cve => cve.severity === 'CRITICAL').slice(0, 5) || [],
            affectedCountries: entities.countries?.slice(0, 10) || []
        };
    };

    const clearDatabase = async () => {
        if (!window.confirm('Are you sure you want to clear all data? This will delete all documents and entities from the database.')) {
            return;
        }

        try {
            // Clear Neo4j database
            const response = await fetch('/api/clear-database', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (response.ok) {
                // Clear local state
                setDocuments([]);
                setSelectedDocument(null);
                setEntities({});
                setEntityCounts({});
                setSearchResults([]);
                setActiveTab('upload');
                
                // Refresh documents list after clearing
                await fetchRecentDocuments();
                
                alert('Database cleared successfully!');
            } else {
                alert('Failed to clear database. Please try again.');
            }
        } catch (error) {
            console.error('Failed to clear database:', error);
            alert('Error clearing database: ' + error.message);
        }
    };

    const handleSearch = async (query) => {
        // If query is empty, clear search results
        if (!query || query.trim() === '') {
            setSearchResults([]);
            return;
        }
        
        try {
            const response = await fetch('/api/search', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ query })
            });

            if (response.ok) {
                const data = await response.json();
                console.log('Search response:', data);
                
                // Transform results into format expected by EntityDashboard
                const formattedResults = [];
                if (data.results && Array.isArray(data.results)) {
                    data.results.forEach(categoryResult => {
                        categoryResult.entities.forEach(entity => {
                            formattedResults.push({
                                entity: entity,
                                category: categoryResult.category,
                                context: `Found in ${categoryResult.category}`
                            });
                        });
                    });
                }
                
                setSearchResults(formattedResults);
            }
        } catch (error) {
            console.error('Search failed:', error);
        }
    };

    const calculateEntityCounts = (entities) => {
        const counts = {};
        Object.keys(entities).forEach(category => {
            if (Array.isArray(entities[category])) {
                counts[category] = entities[category].length;
            }
        });
        return counts;
    };

    const getDocumentContext = () => {
        return {
            documentId: selectedDocument?.id,
            entities,
            entityCounts,
            mitreData: {
                tactics: entities.mitreTactics || [],
                techniques: entities.mitreTechniques || []
            }
        };
    };

    return (
        <div className="App">
            <nav className="navbar navbar-dark bg-dark mb-4">
                <div className="container-fluid">
                    <span className="navbar-brand mb-0 h1">
                        <img src="/images/cipheriq-logo.jpg" alt="CipherIQ" style={{ height: '40px', marginRight: '15px' }} />
                        CipherIQ Intelligence Platform
                    </span>
                    <div className="btn-group">
                        <button 
                            className="btn btn-success btn-sm"
                            onClick={exportPdfReport}
                            title="Export PDF report with heatmaps, geomap, dashboard summary, and data dictionary"
                        >
                            <i className="fas fa-file-pdf me-2"></i>
                            Export PDF Report
                        </button>
                        <button 
                            className="btn btn-info btn-sm"
                            onClick={exportOtelJson}
                            title="Export OTEL compliant JSON with all database nodes and entities"
                        >
                            <i className="fas fa-download me-2"></i>
                            Export OTEL JSON
                        </button>
                        <button 
                            className="btn btn-warning btn-sm"
                            onClick={exportStixJson}
                            title="Export STIX 2.1 compliant JSON with threat intelligence data"
                        >
                            <i className="fas fa-shield-alt me-2"></i>
                            Export STIX JSON
                        </button>
                        <button 
                            className="btn btn-danger btn-sm"
                            onClick={clearDatabase}
                            title="Clear all data from database"
                        >
                            <i className="fas fa-trash-alt me-2"></i>
                            Clear Database
                        </button>
                    </div>
                </div>
            </nav>

            <div className="container-fluid py-3">
                <div className="row">
                    {/* Left Sidebar */}
                    <div className="col-md-3">
                        {/* Tab Navigation */}
                        <ul className="nav nav-pills flex-column mb-3">
                            <li className="nav-item">
                                <button
                                    className={`nav-link w-100 text-start ${activeTab === 'upload' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('upload')}
                                >
                                    <i className="fas fa-upload me-2"></i>
                                    Upload Documents
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link w-100 text-start ${activeTab === 'dashboard' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('dashboard')}
                                    disabled={!selectedDocument}
                                >
                                    <i className="fas fa-chart-bar me-2"></i>
                                    Entity Dashboard
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link w-100 text-start ${activeTab === 'map' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('map')}
                                    disabled={!selectedDocument}
                                >
                                    <i className="fas fa-globe me-2"></i>
                                    Geographic Map
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link w-100 text-start ${activeTab === 'mitre' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('mitre')}
                                    disabled={!selectedDocument}
                                >
                                    <i className="fas fa-fire me-2"></i>
                                    MITRE Heatmap
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link w-100 text-start ${activeTab === 'sparta' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('sparta')}
                                    disabled={!selectedDocument}
                                >
                                    <i className="fas fa-satellite me-2"></i>
                                    SPARTA Heatmap
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link w-100 text-start ${activeTab === 'cyware' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('cyware')}
                                >
                                    <i className="fas fa-shield-alt me-2"></i>
                                    Cyware Intel
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link w-100 text-start ${activeTab === 'chat' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('chat')}
                                >
                                    <i className="fas fa-comments me-2"></i>
                                    Agent Chat
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link w-100 text-start ${activeTab === 'dictionary' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('dictionary')}
                                >
                                    <i className="fas fa-book me-2"></i>
                                    OTEL Dictionary
                                </button>
                            </li>
                        </ul>

                        {/* Recent Documents */}
                        <RecentDocuments
                            documents={documents}
                            selectedDocument={selectedDocument}
                            onDocumentSelect={handleDocumentSelected}
                        />
                    </div>

                    {/* Main Content */}
                    <div className="col-md-9">
                        {activeTab === 'upload' && (
                            <div>
                                <h2 className="mb-4">
                                    <i className="fas fa-upload me-2"></i>
                                    Document Upload & Processing
                                </h2>
                                <DocumentUpload onDocumentProcessed={handleDocumentProcessed} />
                            </div>
                        )}

                        {activeTab === 'dashboard' && selectedDocument && (
                            <div>
                                <h2 className="mb-4">
                                    <i className="fas fa-chart-bar me-2"></i>
                                    Entity Dashboard
                                    <small className="text-muted ms-2">
                                        {selectedDocument.filename}
                                    </small>
                                </h2>
                                <EntityDashboard
                                    entities={entities}
                                    entityCounts={entityCounts}
                                    onSearch={handleSearch}
                                    searchResults={searchResults}
                                />
                            </div>
                        )}

                        {activeTab === 'map' && selectedDocument && (
                            <div>
                                <h2 className="mb-4">
                                    <i className="fas fa-globe me-2"></i>
                                    Geographic Visualization
                                    <small className="text-muted ms-2">
                                        {selectedDocument.filename}
                                    </small>
                                </h2>
                                <GeographicMap
                                    countries={entities.countries || []}
                                    locations={entities.locations || []}
                                />
                            </div>
                        )}

                        {activeTab === 'mitre' && selectedDocument && (
                            <div>
                                <h2 className="mb-4">
                                    <i className="fas fa-fire me-2"></i>
                                    MITRE ATT&CK Heatmap
                                    <small className="text-muted ms-2">
                                        {selectedDocument.filename}
                                    </small>
                                </h2>
                                <MitreHeatmap
                                    tactics={entities.mitreTactics || []}
                                    techniques={entities.mitreTechniques || []}
                                    priorities={entities.priorities || []}
                                />
                            </div>
                        )}

                        {activeTab === 'sparta' && (
                            <div>
                                <h2 className="mb-4">
                                    <i className="fas fa-satellite me-2"></i>
                                    SPARTA Framework Heatmap
                                    {selectedDocument && (
                                        <small className="text-muted ms-2">
                                            {selectedDocument.filename}
                                        </small>
                                    )}
                                </h2>
                                {selectedDocument ? (
                                    <SpartaHeatmap
                                        spartaEntities={{
                                            sparta_techniques: entities.spartaTechniques || entities.sparta_techniques || [],
                                            space_threats: entities.spaceThreats || entities.space_threats || [],
                                            space_assets: entities.spaceAssets || entities.space_assets || []
                                        }}
                                        mitreTechniques={entities.mitreTechniques || entities.mitre_techniques || []}
                                    />
                                ) : (
                                    <div className="alert alert-info">
                                        <i className="fas fa-info-circle me-2"></i>
                                        Please select a document from the sidebar to view SPARTA analysis.
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'cyware' && (
                            <div>
                                <h2 className="mb-4">
                                    <i className="fas fa-shield-alt me-2"></i>
                                    Cyware S-ISAC Threat Intelligence
                                </h2>
                                <CywareIntelDashboard />
                            </div>
                        )}

                        {activeTab === 'chat' && (
                            <div>
                                <h2 className="mb-4">
                                    <i className="fas fa-comments me-2"></i>
                                    Cyber Intelligence Agent
                                    {selectedDocument && (
                                        <small className="text-muted ms-2">
                                            Context: {selectedDocument.filename}
                                        </small>
                                    )}
                                </h2>
                                <AgentChat documentContext={getDocumentContext()} />
                            </div>
                        )}

                        {activeTab === 'dictionary' && (
                            <div>
                                <h2 className="mb-4">
                                    <i className="fas fa-book me-2"></i>
                                    OTEL Data Dictionary
                                </h2>
                                <OtelDataDictionary entities={entities} />
                            </div>
                        )}

                        {!selectedDocument && (activeTab === 'dashboard' || activeTab === 'map' || activeTab === 'mitre' || activeTab === 'sparta') && (
                            <div className="text-center py-5">
                                <i className="fas fa-file-upload fa-3x text-muted mb-3"></i>
                                <h4 className="text-muted">No Document Selected</h4>
                                <p className="text-muted">
                                    Please upload and process a document first, or select from recent documents.
                                </p>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => setActiveTab('upload')}
                                >
                                    <i className="fas fa-upload me-2"></i>
                                    Upload Document
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Status Bar */}
            <div className="bg-light border-top py-2">
                <div className="container-fluid">
                    <div className="row align-items-center">
                        <div className="col-md-6">
                            <small className="text-muted">
                                <i className="fas fa-info-circle me-1"></i>
                                AG-UI Protocol | Powered by llama3.2:1b | RAG-Enhanced NER
                            </small>
                        </div>
                        <div className="col-md-6 text-end">
                            <small className="text-muted">
                                {selectedDocument ? (
                                    <>
                                        <i className="fas fa-file me-1"></i>
                                        {selectedDocument.filename} | 
                                        {Object.keys(entities).reduce((total, key) => 
                                            total + (Array.isArray(entities[key]) ? entities[key].length : 0), 0
                                        )} entities
                                    </>
                                ) : (
                                    'No document selected'
                                )}
                            </small>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default App;
